import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Clock, MapPin, Users } from "lucide-react";
import { fetchParentSessions, type ParentSession, type ParentSessionKid } from "@/lib/api";
import { format } from "date-fns";
import { useState } from "react";
import { cn } from "@/lib/utils";

type AvailabilityStatus = "yes" | "no" | "maybe";

interface AvailabilityRecord {
  sessionId: number;
  kidId: number;
  status: AvailabilityStatus;
}

export default function ParentSchedule() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  // Only needed for the rare case where >1 of the parent's kids share the
  // same batch (e.g. twins) — which kid a given session card's RSVP
  // buttons act on, when there's more than one to choose from. Sessions
  // with exactly one matching kid never touch this.
  const [selectedKidBySession, setSelectedKidBySession] = useState<Record<number, number>>({});

  // staleTime: 0 overrides the app-wide default (queryClient.ts sets
  // staleTime: Infinity globally) on all three queries below — this page
  // shows sessions, kids, and RSVP status that can all change from the
  // coach side (new/edited sessions, batch reassignment) between visits.
  // With the global default, a parent tab left open here would keep
  // showing whatever was fetched on first load, no matter what changed
  // server-side afterward, until a full page reload. The availability
  // mutation below already invalidates its own query on settle, so this
  // mainly matters for changes made elsewhere (another tab, another
  // device, the coach side). Same fix already applied to BatchesPage.tsx's
  // main query for the same reason.
  const { data, isLoading, error } = useQuery({
    queryKey: ["/api/parent/sessions", "upcoming"],
    queryFn: () => fetchParentSessions(true),
    staleTime: 0,
  });

  const sessions: ParentSession[] = data?.sessions || [];

  // Fetch parent's kids (used only to decide whether to show the
  // "RSVP for {name}" attribution label — hidden when the parent only has
  // one kid, since it'd be redundant).
  const { data: kidsData } = useQuery({
    queryKey: ["/api/parent/kids"],
    queryFn: async () => {
      const res = await fetch("/api/parent/kids", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch kids");
      return res.json();
    },
    staleTime: 0,
  });

  const kids = kidsData?.data || [];
  const parentHasMultipleKids = kids.length > 1;

  // Fetch availability for all sessions
  const { data: availabilityData } = useQuery({
    queryKey: ["/api/parent/availability"],
    queryFn: async () => {
      const res = await fetch("/api/parent/availability", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch availability");
      return res.json();
    },
    staleTime: 0,
  });

  const availabilityMap = new Map<string, AvailabilityStatus>();
  if (availabilityData?.availability) {
    availabilityData.availability.forEach((record: AvailabilityRecord) => {
      const key = `${record.sessionId}-${record.kidId}`;
      availabilityMap.set(key, record.status);
    });
  }

  // Mutation for updating RSVP — kidId is always the specific kid this
  // session card is attributed to (from session.kids, or the parent's pick
  // in the rare multi-kid-same-batch case), never assumed.
  const updateRSVPMutation = useMutation({
    mutationFn: async ({ sessionId, kidId, status }: { sessionId: number; kidId: number; status: AvailabilityStatus }) => {
      const res = await fetch(`/api/parent/sessions/${sessionId}/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ kidId, status }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || errorData.message || "Failed to update RSVP");
      }
      return res.json();
    },
    onMutate: async ({ sessionId, kidId, status }) => {
      const key = `${sessionId}-${kidId}`;
      setUpdatingKey(key);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/parent/availability"] });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(["/api/parent/availability"]);

      // Optimistically update
      queryClient.setQueryData(["/api/parent/availability"], (old: any) => {
        if (!old?.availability) return old;

        const existing = old.availability.find((a: AvailabilityRecord) =>
          a.sessionId === sessionId && a.kidId === kidId
        );
        if (existing) {
          return {
            ...old,
            availability: old.availability.map((a: AvailabilityRecord) =>
              a.sessionId === sessionId && a.kidId === kidId ? { ...a, status } : a
            ),
          };
        } else {
          return {
            ...old,
            availability: [...old.availability, { sessionId, kidId, status }],
          };
        }
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(["/api/parent/availability"], context.previousData);
      }
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to update RSVP",
        variant: "destructive",
      });
      setUpdatingKey(null);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "RSVP updated successfully",
      });
      setUpdatingKey(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/parent/availability"] });
    },
  });

  const handleRSVPUpdate = (sessionId: number, kidId: number, status: AvailabilityStatus) => {
    updateRSVPMutation.mutate({ sessionId, kidId, status });
  };

  const getAvailabilityBadge = (status: AvailabilityStatus) => {
    switch (status) {
      case "yes":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100" data-testid="rsvp-badge-going">Going</Badge>;
      case "no":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100" data-testid="rsvp-badge-not-going">Not Going</Badge>;
      case "maybe":
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100" data-testid="rsvp-badge-unsure">Unsure</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl" data-testid="parent-schedule-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Upcoming Sessions</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          View all scheduled training sessions and events
        </p>
      </div>

      {/* Upcoming Sessions List */}
      <Card data-testid="upcoming-sessions-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Schedule
          </CardTitle>
          <CardDescription>All upcoming sessions for your kids</CardDescription>
        </CardHeader>
        <CardContent data-testid="upcoming-sessions-list">
          {isLoading ? (
            <div className="text-center py-8" data-testid="loading-sessions">Loading sessions...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600" data-testid="error-sessions">
              Error loading sessions. Please try again.
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-sessions">
              No upcoming sessions scheduled
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => {
                // session.kids is server-computed (parent-availability.ts's
                // GET /api/parent/sessions): the parent's own kid(s) whose
                // batch matches this session. Almost always exactly one —
                // more than one only when two of the parent's kids share a
                // batch (e.g. twins), which is what the pill selector below
                // is for. Zero shouldn't happen (the endpoint only returns
                // sessions matching at least one kid's batch) but is
                // handled defensively rather than assumed away.
                const sessionKids = session.kids ?? [];
                const selectedKidId = selectedKidBySession[session.id] ?? sessionKids[0]?.id ?? null;
                const kid = sessionKids.find((k: ParentSessionKid) => k.id === selectedKidId) ?? null;
                const availabilityKey = kid ? `${session.id}-${kid.id}` : null;
                const currentStatus = availabilityKey ? availabilityMap.get(availabilityKey) || "maybe" : "maybe";
                const isUpdating = availabilityKey === updatingKey;

                return (
                  <div
                    key={session.id}
                    className="border rounded-lg p-3 sm:p-4 space-y-3"
                    data-testid={`session-card-${session.id}`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-base sm:text-lg break-words flex-1">{session.title}</div>
                        {getAvailabilityBadge(currentStatus)}
                      </div>
                      {session.description && (
                        <p className="text-sm text-muted-foreground break-words">{session.description}</p>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                      <div className="flex items-center gap-1 min-w-0">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{format(new Date(session.startTime), "MMM dd, yyyy")}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{format(new Date(session.startTime), "h:mm a")} - {format(new Date(session.endTime), "h:mm a")}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{session.location}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <Users className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{session.ageGroup}</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t space-y-2">
                      <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                        {session.sessionType}
                      </div>

                      {sessionKids.length > 1 && (
                        <div className="flex flex-wrap gap-1" data-testid={`kid-selector-${session.id}`}>
                          {sessionKids.map((k: ParentSessionKid) => (
                            <button
                              key={k.id}
                              type="button"
                              onClick={() => setSelectedKidBySession((prev) => ({ ...prev, [session.id]: k.id }))}
                              data-testid={`kid-pill-${session.id}-${k.id}`}
                              className={cn(
                                "px-2 py-1 rounded-full text-xs font-medium border transition-colors",
                                k.id === selectedKidId
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-muted-foreground border-input hover:bg-muted"
                              )}
                            >
                              {k.firstName}
                            </button>
                          ))}
                        </div>
                      )}

                      {kid && sessionKids.length === 1 && parentHasMultipleKids && (
                        <p className="text-xs text-muted-foreground">RSVP for {kid.firstName}</p>
                      )}

                      {kid && (
                        <div className="flex flex-col sm:flex-row gap-2" data-testid={`rsvp-actions-${session.id}`}>
                          <Button
                            size="sm"
                            variant={currentStatus === "yes" ? "default" : "outline"}
                            onClick={() => handleRSVPUpdate(session.id, kid.id, "yes")}
                            disabled={isUpdating}
                            data-testid={`btn-rsvp-going-${session.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            Going
                          </Button>
                          <Button
                            size="sm"
                            variant={currentStatus === "no" ? "default" : "outline"}
                            onClick={() => handleRSVPUpdate(session.id, kid.id, "no")}
                            disabled={isUpdating}
                            data-testid={`btn-rsvp-not-going-${session.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            Not Going
                          </Button>
                          <Button
                            size="sm"
                            variant={currentStatus === "maybe" ? "default" : "outline"}
                            onClick={() => handleRSVPUpdate(session.id, kid.id, "maybe")}
                            disabled={isUpdating}
                            data-testid={`btn-rsvp-unsure-${session.id}`}
                            className="flex-1 sm:flex-none"
                          >
                            Unsure
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
